import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import type { SelectorTriggerProps } from "@/v2-backoffice-client/components/selector"
import { useCustomerQuery } from "@/v2-backoffice-client/customer/customer.query"
import { useCustomerViewQuery } from "@/v2-backoffice-client/customer/view/customer.view.query"
import TenantCustomerAddSelectorItem from "@/v2-backoffice-client/tenant/customer/add/TenantCustomerAddSelectorItem"

export type TenantCustomerAddSelectorProps = SelectorTriggerProps & {
  tenantId: number
  externalCustomerId?: string | null | undefined
}

function TenantCustomerAddSelector(props: TenantCustomerAddSelectorProps) {
  const { tenantId, externalCustomerId, ...triggerProps } = props

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data: currentData } = useCustomerViewQuery({
    skip: !isOpen || !externalCustomerId,
    variables: { externalId: externalCustomerId ?? "" },
  })

  const { data, loading, error } = useCustomerQuery({
    skip: !isOpen || search.length < 2,
    variables: {
      input: {
        limit: 10,
        filters:
          search.length >= 2
            ? [{ column: "Name", contains: search }]
            : undefined,
      },
    },
  })

  errorToast(error, {
    title: "Failed to query for customers",
  })

  const activeCustomerId = selectedId ?? externalCustomerId
  const searchResults = data?.getCustomers.items ?? []
  const currentCustomer = currentData?.getCustomerByExternalId
  const filteredResults = searchResults.filter(
    (c) => c.Id !== currentCustomer?.Id,
  )

  return (
    <Selector
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) setSelectedId(null)
      }}
    >
      <Selector.Trigger iconSrc="plus-small" {...triggerProps}>
        {triggerProps.children}
      </Selector.Trigger>

      <Selector.Content>
        <Selector.Search
          placeholder="Search by customer name..."
          onSearch={setSearch}
        />

        <Selector.List
          emptyLabel="No customers found"
          isLoading={loading}
          error={error}
          noResults={filteredResults.length === 0}
          pinned={
            currentCustomer && (
              <TenantCustomerAddSelectorItem
                customer={currentCustomer}
                added={currentCustomer.ExternalCustomerId === activeCustomerId}
                tenantId={tenantId}
                onSelect={() => {
                  setSelectedId(currentCustomer.ExternalCustomerId ?? null)
                }}
                onDeselect={() => {
                  setSelectedId(null)
                }}
              />
            )
          }
        >
          {filteredResults.map((customer) => (
            <TenantCustomerAddSelectorItem
              key={customer.Id}
              customer={customer}
              added={customer.ExternalCustomerId === activeCustomerId}
              tenantId={tenantId}
              onSelect={() => {
                setSelectedId(customer.ExternalCustomerId ?? null)
              }}
              onDeselect={() => {
                setSelectedId(null)
              }}
            />
          ))}
        </Selector.List>
      </Selector.Content>
    </Selector>
  )
}

export default TenantCustomerAddSelector
